import { useEffect, useRef, useState } from 'react';
import { useCesiumContext } from "../cesium/CesiumContext";
import type { Layout } from 'plotly.js';
import { formatUnitValue } from "../../lib/convert";
import { cartoPosToString } from './VisibilityInfo';
import { isDefined } from "../../lib/utils";
import { useClockOnTick } from "../clock";
import { getEntityName } from "./TrackedEntityMenu";
import {
    JulianDate,
} from "@cesium/engine";
//@ts-ignore
import createPlotlyComponent from "react-plotly.js/factory";
//@ts-ignore
import Plotly from 'plotly.js/lib/index-basic';
const Plot = createPlotlyComponent(Plotly);


// Define a type for the component props
type VisibilityPlotProps = {
    plotTitle: string;
    csvData: string[][];
    nRows: number;
    trackingViewers: [string | undefined, string][];
    eclipseData: [string[], (number | null)[]]; // Optional prop for additional data, define this type more specifically based on what you expect
    timeStep?: string;
    numSteps?: number;
    currentStep?: number;
    execTime?: string;
    csvUrl?: string;
}

type Trace = {
    type: string;
    mode: string;
    name: string;
    x: number[] | string[];
    y: number[];
    line?: { color: string };
    hovertemplate?: string;
    fill?: string;
    fillcolor?: string;
    hoverinfo?: string;
    visible?: string;
    yaxis?: string;
}

const entityTraceColors: { [key: string]: string } = {
    ITEM1: "rgba(71, 214, 157, 0.6)",
    ITEM2: "rgba(237, 167, 123, 0.6)",
    ITEM3: "rgb1(237, 135, 142, 0.6)",
}

function VisibilityPlot({ plotTitle, csvData, nRows, trackingViewers, eclipseData, timeStep, numSteps, execTime, currentStep, csvUrl }: VisibilityPlotProps) {
    const [plotData, setPlotData] = useState<Trace[]>([]);
    const [layout, setLayout] = useState<Partial<Layout>>({
        title: plotTitle,
        xaxis: {
            title: {
                text: 'Time (UTC)',
            },
            hoverformat: '%Y-%m-%d %H:%M:%S',
        },
        yaxis: {
            title: {
                text: 'Elevation (deg)',
            },
        },
        yaxis2: {
            title: {
                text: 'Azimuth (deg)',
            },
            overlaying: 'y',
            side: 'right'
        },
        legend: {
            x: 1.15,
        },
        hoverlabel: { bgcolor: "#FFF" },
        hovermode: 'x unified',
        width: 800
    })
    const { map } = useCesiumContext();
    const markerISOTimeRef = useRef("");

    useEffect(() => {
        if (!csvData.length) return;
        
        // UTC times
        const csvHeader = csvData[0];
        const UTCTimeIdx = csvHeader.indexOf("UTC");
        // data
        const sunEleIdx = csvHeader.indexOf("SUN_ELE");
        const earthEleIdx = csvHeader.indexOf("EARTH_ELE");
        const sunVisIdx = csvHeader.indexOf("SUN_VIS");
        const earthVisIdx = csvHeader.indexOf("EARTH_VIS");
        const sunAzmtIdx = csvHeader.indexOf("SUN_AZMT");
        const earthAzmtIdx = csvHeader.indexOf("EARTH_AZMT");
        const pattern = /ENTITY_.*_VIS$/;
        const entityVisIndexes = 
            csvHeader.map((item, index) => pattern.test(item) ? index : -1)
            .filter(index => index !== -1);
        // camera pos
        const cameraLatIdx = csvHeader.indexOf("CAMERA_LAT");
        const cameraLonIdx = csvHeader.indexOf("CAMERA_LON");
        const cameraHIdx = csvHeader.indexOf("CAMERA_HEIGHT");
        
        const UTCTimes = [];
        const sunEleList:number[] = [];
        const earthEleList:number[] = [];
        const sunVisList = [];
        const earthVisList = [];
        const combVisList = [];
        const sunAzmtList = [];
        const earthAzmtList = [];
        let entitiesVisList: number[][] = [];
        const camLatList = [];
        const camLonList = [];
        const camHList = [];
        let sunEle: number, earthEle: number, sunVis: number, earthVis: number, combVis: number, entitiesVisData: number[];
        for(let r = 1; r < nRows; r++) {
            const rowData = csvData[r];
            UTCTimes.push(rowData[UTCTimeIdx]);
            sunEle = parseFloat(rowData[sunEleIdx])
            sunEleList.push(sunEle);
            earthEle = parseFloat(rowData[earthEleIdx])
            earthEleList.push(earthEle);
            sunVis = parseFloat(rowData[sunVisIdx]);
            sunVisList.push(sunVis);
            earthVis = parseFloat(rowData[earthVisIdx]);
            earthVisList.push(earthVis);
            combVis = sunVis && earthVis ? 1 : 0;
            combVisList.push(combVis);
            sunAzmtList.push(parseFloat(rowData[sunAzmtIdx]));
            earthAzmtList.push(parseFloat(rowData[earthAzmtIdx]));
            camLatList.push(parseFloat(rowData[cameraLatIdx]));
            camLonList.push(parseFloat(rowData[cameraLonIdx]));
            camHList.push(parseFloat(rowData[cameraHIdx]));
            if (entityVisIndexes.length > 0) {
                entitiesVisData = entityVisIndexes.map(index => parseFloat(rowData[index]));
                entitiesVisData.forEach((elem, index) => {
                    if (!entitiesVisList[index]) {
                        // initialization
                        entitiesVisList[index] = [];
                    }
                    entitiesVisList[index].push(elem);
                });
            }
        }
        const maxSunEle = Math.max(...sunEleList);
        const maxEarthEle = Math.max(...earthEleList);
        const sunVisListN = sunVisList.map( (x, idx) => x * sunEleList[idx]);
        const earthVisListN = earthVisList.map( (x, idx) => x * earthEleList[idx]);
        const maxCombValue = Math.max(maxSunEle, maxEarthEle);
        const combVisListN = combVisList.map( x => x * maxCombValue);

        const basichovertemplate = "%{y:.2f} (deg)";
        
        const sunEleTrace: Trace = {
            type: "scatter",
            mode: "lines",
            name: "Sun Elevation",
            x: UTCTimes,
            y: sunEleList,
            line: {color: '#ff5c33'},
            hovertemplate:
                    basichovertemplate
        };

        const earthEleTrace: Trace = {
            type: "scatter",
            mode: "lines",
            name: "Earth Elevation",
            x: UTCTimes,
            y: earthEleList,
            line: {color: '#7198c4'},
            hovertemplate:
                    basichovertemplate
        };

        const sunVisTrace: Trace = {
            type: "scatter",
            name: "Sun Visibility",
            x: UTCTimes,
            y: sunVisListN,
            fill: 'tozeroy',
            fillcolor: 'rgba(255, 92, 51, 0.6)',
            mode: 'none',
            hoverinfo: 'skip',
            visible: "legendonly",
        };

        const earthVisTrace: Trace = {
            type: "scatter",
            name: "Earth Visibility",
            x: UTCTimes,
            y: earthVisListN,
            fill: 'tozeroy',
            fillcolor: 'rgba(113, 152, 196, 0.6)',
            mode: 'none',
            hoverinfo: 'skip',
            visible: "legendonly",
        };

        const combVisTrace: Trace = {
            type: "scatter",
            name: "Sun&Earth Visibility",
            x: UTCTimes,
            y: combVisListN,
            fill: 'tozeroy',
            fillcolor: 'rgba(152,251,152,0.6)',
            mode: 'none',
            hoverinfo: 'skip',
        };

        const sunAzmtTrace: Trace = {
            type: "scatter",
            mode: "lines",
            name: "Sun Azimuth (0°N)",
            x: UTCTimes,
            y: sunAzmtList,
            line: {color: '#f0ce60'},
            yaxis: "y2",
            hovertemplate:
                    basichovertemplate,
            visible: "legendonly",
        };
        const earthAzmtTrace: Trace = {
            type: "scatter",
            mode: "lines",
            name: "Earth Azimuth (0°N)",
            x: UTCTimes,
            y: earthAzmtList,
            line: {color: '#79bd8b'},
            yaxis: "y2",
            hovertemplate:
                    basichovertemplate,
            visible: "legendonly",
        };

        const data = [sunVisTrace, earthVisTrace, combVisTrace, sunEleTrace, earthEleTrace, sunAzmtTrace, earthAzmtTrace];

        if (eclipseData.length > 0) {
            const eclipseUTCList = eclipseData[0];
            const sunEarthAngle = eclipseData[1];
            
            // Splitting trace into segments with null values
            const segments: { x: string[]; y: number[] }[] = [];
            let currentSegment: { x: string[]; y: number[] } = { x: [], y: [] };
        
            for (let i = 0; i < sunEarthAngle.length; i++) {
                if (!sunEarthAngle[i]) {
                    // Reached a gap, push the current segment and start a new one
                    if (currentSegment.x.length > 0) {
                        segments.push(currentSegment);
                    }
                    currentSegment = { x: [], y: [] };
                } else {
                    // Add the point to the current segment
                    currentSegment.x.push(eclipseUTCList[i]);
                    currentSegment.y.push(maxCombValue);
                }
            }
        
            // Push the last segment
            if (currentSegment.x.length > 0) {
                segments.push(currentSegment);
            }
        
            // add eclipse trace
            const earthSunEclipseTrace = segments.map((segment, idx, arr) => {
                let solarEclipseName = `Solar Eclipse`;
                if (arr.length > 1) {
                    solarEclipseName += ` ${idx}`;
                }
                return {
                    type: "scatter",
                    name: solarEclipseName,
                    x: segment.x,
                    y: segment.y,
                    fill: 'tozeroy',
                    fillcolor: 'rgba(0,0,0,0.6)',
                    mode: 'none',
                    hoverinfo: 'skip',
                }
            });
            
            data.push(...earthSunEclipseTrace);
        }
        
        for(let ee = 0; ee < entitiesVisList.length; ee++) {
            const entityVisListN = entitiesVisList[ee].map( x => x * maxCombValue);
            const entity = map.getEntity({datasourceId: trackingViewers[ee][0], entityId: trackingViewers[ee][1]});
            const entityName = entity ? getEntityName(entity) : csvHeader[entityVisIndexes[ee]].split("_")[1];
            const traceColor = entityTraceColors[`ITEM${((ee + 1) % 3) + 1}`];
            
            // add entity visualization
            const entityVisTrace:Trace = {
                type: "scatter",
                name: entityName,
                x: UTCTimes,
                y: entityVisListN,
                fill: 'tozeroy',
                fillcolor: traceColor,
                mode: 'none',
                hoverinfo: 'skip',
            }
            // prepend to data
            data.unshift(entityVisTrace);
        }
    
        // check whether the camera position has changed
        const camMoving = 
            camLatList[0] !== camLatList[1] ||
            camLonList[0] !== camLonList[1] ||
            camHList[0] !== camHList[1];
    
        if (!camMoving) {
            const cameraCartoPos = map.getCameraCartoPos();
            const cameraCartoPosS = cartoPosToString(cameraCartoPos);
            const cameraGH = isDefined(map.terrainHeightAtPos) ? (cameraCartoPos.height - map.terrainHeightAtPos) : NaN;
            const to = cameraGH > 1000 ? "km" : "m"
            const precision = to === "m" ? 1 : 3;
            const cameraGHLBL = formatUnitValue(cameraGH, {
                from: "m",
                to,
                precision,
                showUnits: true
              }) ?? "-";
            // show camera position info in the title
            const newTitle = plotTitle + `<br><sub>Lat,Lon,H: ${cameraCartoPosS.join(", ")} km&nbsp;&nbsp;&nbsp;Hg: ${cameraGHLBL}</sub>`;
            if (newTitle !== layout.title)  {
                const newLayout = {
                    ...layout,
                    title: newTitle,
                }
                setLayout(newLayout);
            }
        }
        setPlotData(data);
    }, [map, csvData, nRows, eclipseData, plotTitle, layout]);

    useClockOnTick(() => {
        // get the y values
        if (!isDefined(plotData) || plotData[0]?.y.length < 2) {
            // no data
            return;
        }
        if (!layout?.yaxis?.range?.length) {
            return;
        }
        const isoDateTime = JulianDate.toIso8601(map.clock.currentTime);
        if (isoDateTime === markerISOTimeRef.current)
            return;

        // make y0,y1 a bit smaller as they where higher than the actual range
        let y0 = Math.min(...layout.yaxis.range);
        y0 -= y0 * 0.1;
        let y1 = Math.max(...layout.yaxis.range);
        y1 -= y1 * 0.1;
        // Update the layout with the marker line position
        const newLayout:Partial<Layout> = {
            ...layout,
            shapes: [
                {
                type: 'line',
                xref: 'x',
                yref: 'y',
                x0: isoDateTime,
                y0: y0,
                x1: isoDateTime,
                y1: y1,
                line: {
                    color: 'black',
                    width: 1,
                    dash: 'dash',
                }
                }
            ]
        };
        setLayout(newLayout);
        markerISOTimeRef.current = isoDateTime;
    }, 0);

    return (
        <>
            <div className='bg-white'>
                <Plot
                    data={plotData}
                    layout={layout}
                />
                <div className="bg-white border-solid p-1 border-qmdark-100 text-black font-extralight text-s border-t">
                    <b>Time Step Used</b>: {timeStep} - <b># of Steps</b>: {numSteps}
                    {isDefined(currentStep) && currentStep > 0 ? (
                        <>
                           {" -"} <b>Current Step #</b>: {currentStep}
                        </>
                    ) : null}
                    {(isDefined(execTime) && execTime.length > 0) ? ( 
                        <>
                           {" -"} <b>Execution Time</b>: {execTime}
                        </>
                    ): null}
                    {(isDefined(csvUrl) && csvUrl.length > 0) ? ( 
                        <div>
                           <a className="text-black underline" href={csvUrl} download='visibility_info.csv'>Download csv</a>
                        </div>
                    ): null}
                </div>
            </div>
        </>
    );
}

export default VisibilityPlot;
